// src/layouts/components/DynamicAppView.jsx

import { useMemo, Suspense } from "react";
import { Navigate, useParams, useSearchParams } from "react-router-dom";

import { useAuth } from "../../auth/authContext";
import { loadAppComponent } from "../../data/appLoader";
import { appRegistry } from "../../data/appRegistry";
import useCatalogEntry from "../../catalog/hooks/useCatalogEntry";
import useCatalogPathwayResolution from "../../catalog/hooks/useCatalogPathwayResolution";
import {
  canonicalPathwayRouteForLegacyViewId,
  catalogPathwayCompatibilityForLegacyCatalogSlug,
  isPilotCatalogPathwaySlug
} from "../../catalog/launch/catalogPathwayCompatibility";
import {
  getPreferredLaunchConfigurationIdForEntry,
  resolveLaunchConfigurationSelection
} from "../../catalog/launch/catalogLaunchConfigurations";
import { CatalogLaunchProvider } from "../../catalog/launch/CatalogLaunchContext";
import ProjectDashboard from "../../features/projects/views/ProjectDashboard";

export default function DynamicAppView() {
  const {
    slug: catalogEntrySlug,
    viewId: directViewId
  } = useParams();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const userRole = user?.role || "standard_user";
  const isCatalogLaunch = Boolean(catalogEntrySlug);
  const legacyCatalogCompatibility =
    isCatalogLaunch
      ? catalogPathwayCompatibilityForLegacyCatalogSlug(catalogEntrySlug)
      : null;

  const launchParamString = searchParams.toString();
  const launchParams = useMemo(
    () => Object.fromEntries(new URLSearchParams(launchParamString).entries()),
    [launchParamString]
  );
  const requestedLaunchConfigurationId =
    launchParams.launchConfig || null;
  const usesCatalogPathwayResolution =
    isCatalogLaunch &&
    !legacyCatalogCompatibility &&
    isPilotCatalogPathwaySlug(
      catalogEntrySlug,
      requestedLaunchConfigurationId
    );
  const directLegacyCompatibilityRoute =
    !isCatalogLaunch && directViewId
      ? canonicalPathwayRouteForLegacyViewId(directViewId)
      : null;

  const {
    entry: catalogEntry,
    loading,
    error
  } = useCatalogEntry(catalogEntrySlug, {
    enabled:
      isCatalogLaunch &&
      !legacyCatalogCompatibility,
    includeDirectLinkOnly:
      isCatalogLaunch &&
      !legacyCatalogCompatibility
  });
  const {
    result: catalogPathwayResolution,
    loading: pathwayLoading,
    error: pathwayError
  } = useCatalogPathwayResolution(catalogEntrySlug, {
    enabled: usesCatalogPathwayResolution,
    launchConfigurationId: requestedLaunchConfigurationId
  });

  if (directLegacyCompatibilityRoute) {
    const [pathname, search = ""] =
      directLegacyCompatibilityRoute.split("?");

    return (
      <Navigate
        replace
        to={{
          pathname,
          search: search ? `?${search}` : ""
        }}
      />
    );
  }

  if (legacyCatalogCompatibility) {
    const targetParams = new URLSearchParams(searchParams);
    targetParams.set(
      "launchConfig",
      legacyCatalogCompatibility.launchConfigurationId
    );

    return (
      <Navigate
        replace
        to={{
          pathname: `/app/${legacyCatalogCompatibility.canonicalSlug}/open`,
          search: `?${targetParams.toString()}`
        }}
      />
    );
  }

  if (usesCatalogPathwayResolution && (pathwayLoading || loading)) {
    return <p>Loading...</p>;
  }

  if (usesCatalogPathwayResolution && (pathwayError || error)) {
    return <p>{pathwayError || error}</p>;
  }

  if (isCatalogLaunch && !usesCatalogPathwayResolution && loading) {
    return <p>Loading...</p>;
  }

  if (isCatalogLaunch && !usesCatalogPathwayResolution && error) {
    return <p>{error}</p>;
  }

  const authorizedPathwayViewId =
    catalogPathwayResolution?.authorizedPathway?.locator?.viewId;
  const isCatalogProjectLaunch =
    isCatalogLaunch &&
    !usesCatalogPathwayResolution &&
    catalogEntry?.launch?.type === "project";
  const {
    selectedConfiguration,
    requestedConfiguration,
    hasRequestedSelection
  } = resolveLaunchConfigurationSelection(
    catalogEntry,
    requestedLaunchConfigurationId,
    {
      userRole,
      preferredLaunchConfigurationId:
        getPreferredLaunchConfigurationIdForEntry(catalogEntry)
    }
  );

  const resolvedLaunch =
    selectedConfiguration?.launch ||
    catalogEntry?.launch ||
    null;

  if (
    isCatalogLaunch &&
    !usesCatalogPathwayResolution &&
    hasRequestedSelection &&
    !requestedConfiguration
  ) {
    return <p>You do not have access to this catalog entry.</p>;
  }

  const viewId = usesCatalogPathwayResolution
    ? authorizedPathwayViewId
    : isCatalogLaunch
      ? resolvedLaunch?.viewId
      : directViewId;

  if (
    usesCatalogPathwayResolution &&
    (
      catalogPathwayResolution?.authorized !== true ||
      !authorizedPathwayViewId
    )
  ) {
    return <p>You do not have access to this catalog entry.</p>;
  }

  if (
    isCatalogLaunch &&
    !usesCatalogPathwayResolution &&
    (
      !catalogEntry ||
      (
        catalogEntry.launch?.type === "appView"
          ? !viewId
          : catalogEntry.launch?.type === "project"
            ? !catalogEntry.source?.slug
            : true
      )
    )
  ) {
    return <p>Not found</p>;
  }

  const item = isCatalogProjectLaunch
    ? null
    : appRegistry.find((view) => view.id === viewId);

  if (!isCatalogProjectLaunch && !item) {
    return <p>Not found</p>;
  }

  const userId =
    user?._id || null;

  const roleAllowed =
    isCatalogProjectLaunch ||
    !item.allowedRoles ||
    item.allowedRoles.includes(userRole);

  const userAllowed =
    isCatalogProjectLaunch ||
    !item.allowedUsers ||
    item.allowedUsers.includes(userId);

  if (!roleAllowed || !userAllowed) {
    return <p>You do not have access to this page.</p>;
  }

  const Component = isCatalogProjectLaunch
    ? ProjectDashboard
    : loadAppComponent(item.component);

  if (!Component) {
    return <p>Not found</p>;
  }

  return (
    <CatalogLaunchProvider
      source={isCatalogLaunch ? "catalog" : "direct"}
      surface="app"
      viewId={viewId}
      catalogEntry={isCatalogLaunch && !usesCatalogPathwayResolution
        ? {
            ...catalogEntry,
            launch: resolvedLaunch,
            metadata: {
                ...(catalogEntry?.metadata || {}),
                ux: {
                  ...(catalogEntry?.metadata?.ux || {}),
                  launchConfigurations:
                    catalogEntry?.metadata?.ux
                      ?.launchConfigurations || []
                }
              }
            }
        : usesCatalogPathwayResolution
          ? catalogEntry
          : null}
      loading={usesCatalogPathwayResolution ? pathwayLoading : loading}
      error={usesCatalogPathwayResolution ? pathwayError : error}
      viewDefinition={item}
      launchParams={launchParams}
      selectedLaunchConfigurationId={usesCatalogPathwayResolution
        ? requestedLaunchConfigurationId
        : selectedConfiguration?.id || null}
      backLabel="Back to catalog"
      backTo="/app/catalog"
    >
      <Suspense fallback={<p>Loading...</p>}>
        <Component
          {...(isCatalogProjectLaunch
            ? { projectSlug: catalogEntry.source.slug }
            : {})}
        />
      </Suspense>
    </CatalogLaunchProvider>
  );
}
